import * as React from 'react';
import { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { formatPhoneInput, stripPhoneToDigits } from '@/lib/phoneUtils';

interface PhoneInputProps {
    value: string;
    onChange: (rawValue: string) => void;
    placeholder?: string;
    disabled?: boolean;
    className?: string;
    id?: string;
    required?: boolean;
}

/** Returns true if the digit string matches a US phone pattern that can be auto-formatted. */
function isUsPattern(digits: string): boolean {
    return digits.length <= 10 || (digits.length === 11 && digits.startsWith('1'));
}

function toDisplay(value: string): string {
    const digits = stripPhoneToDigits(value);
    return isUsPattern(digits) ? formatPhoneInput(value) : value;
}

/** Place the cursor after the Nth digit in a formatted phone string.
 *  Skips past following formatting chars so the caret sits before the next digit.
 */
function cursorAfterDigitIndex(formatted: string, digitIndex: number): number {
    if (digitIndex <= 0) {
        let pos = 0;
        // Land inside a leading "(" rather than before it
        if (formatted[0] === '(') pos = 1;
        return pos;
    }
    let seen = 0;
    let pos = formatted.length;
    for (let i = 0; i < formatted.length; i++) {
        if (/\d/.test(formatted[i])) {
            seen++;
            if (seen === digitIndex) {
                pos = i + 1;
                break;
            }
        }
    }
    while (pos < formatted.length && !/\d/.test(formatted[pos])) {
        pos++;
    }
    return pos;
}

export function PhoneInput({
    value,
    onChange,
    placeholder = '(555) 555-5555',
    disabled = false,
    className = '',
    id,
    required,
}: PhoneInputProps) {
    const [displayValue, setDisplayValue] = useState(() => toDisplay(value || ''));
    const prevDigitsRef = useRef(stripPhoneToDigits(value || ''));
    const inputRef = useRef<HTMLInputElement>(null);
    const pendingCursorRef = useRef<number | null>(null);

    useEffect(() => {
        const newDigits = stripPhoneToDigits(value || '');
        const currentDigits = stripPhoneToDigits(displayValue);
        if (newDigits === currentDigits) {
            return;
        }
        setDisplayValue(toDisplay(value || ''));
        prevDigitsRef.current = newDigits;
    }, [value, displayValue]);

    // Restore caret after React commits the reformatted value (avoids jump-to-end).
    useLayoutEffect(() => {
        if (pendingCursorRef.current === null || !inputRef.current) return;
        const pos = pendingCursorRef.current;
        pendingCursorRef.current = null;
        inputRef.current.setSelectionRange(pos, pos);
    }, [displayValue]);

    const applyChange = (digits: string, formatted: string, cursorAfterDigit?: number) => {
        prevDigitsRef.current = digits;
        if (cursorAfterDigit !== undefined) {
            pendingCursorRef.current = cursorAfterDigitIndex(formatted, cursorAfterDigit);
        }
        setDisplayValue(formatted);
        onChange(digits);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const raw = e.target.value;
        const cursorPos = e.target.selectionStart ?? raw.length;
        const digits = stripPhoneToDigits(raw);
        const digitsBeforeCursor = stripPhoneToDigits(raw.slice(0, cursorPos)).length;

        // The edit removed a character but no digit — the user backspaced over formatting
        // (the ") " after the area code, or the "-"). Reformatting would put it straight back,
        // so drop the digit before the caret instead. handleKeyDown covers this on desktop, but
        // soft keyboards often report Backspace as a plain input event with no usable key.
        if (raw.length < displayValue.length && digits.length === prevDigitsRef.current.length && digitsBeforeCursor > 0) {
            const removeIndex = digitsBeforeCursor - 1;
            const newDigits = digits.slice(0, removeIndex) + digits.slice(removeIndex + 1);
            applyChange(newDigits, formatPhoneInput(newDigits), removeIndex);
            return;
        }

        if (isUsPattern(digits)) {
            applyChange(digits, formatPhoneInput(digits), digitsBeforeCursor);
        } else {
            prevDigitsRef.current = digits;
            setDisplayValue(raw);
            onChange(digits);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key !== 'Backspace' && e.key !== 'Delete') return;

        const input = e.currentTarget;
        const selectionStart = input.selectionStart ?? 0;
        const selectionEnd = input.selectionEnd ?? 0;
        if (selectionStart !== selectionEnd) return;

        const digits = stripPhoneToDigits(displayValue);
        if (!digits || !isUsPattern(digits)) return;

        if (e.key === 'Backspace') {
            if (selectionStart === 0) return;

            const charBefore = displayValue[selectionStart - 1];
            if (/\d/.test(charBefore)) return;

            e.preventDefault();
            const digitsBeforeCursor = stripPhoneToDigits(displayValue.slice(0, selectionStart)).length;
            if (digitsBeforeCursor === 0) return;

            const removeIndex = digitsBeforeCursor - 1;
            const newDigits = digits.slice(0, removeIndex) + digits.slice(removeIndex + 1);
            applyChange(newDigits, formatPhoneInput(newDigits), removeIndex);
            return;
        }

        // Delete key — cursor is on a formatting character, remove the following digit
        if (selectionStart >= displayValue.length) return;

        const charAt = displayValue[selectionStart];
        if (/\d/.test(charAt)) return;

        e.preventDefault();
        const digitsBeforeCursor = stripPhoneToDigits(displayValue.slice(0, selectionStart)).length;
        if (digitsBeforeCursor >= digits.length) return;

        const newDigits = digits.slice(0, digitsBeforeCursor) + digits.slice(digitsBeforeCursor + 1);
        applyChange(newDigits, formatPhoneInput(newDigits), digitsBeforeCursor);
    };

    return (
        <input
            ref={inputRef}
            type="tel"
            inputMode="tel"
            id={id}
            value={displayValue}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled}
            required={required}
            className={className}
        />
    );
}
